import { useCallback, useEffect, useRef } from 'react';

type DebouncedFunction<T extends (...args: any[]) => void> = (
  ...args: Parameters<T>
) => void;

// this custom hook will return function that runs a callback after debouncing
function useDebounceCallback<T extends (...args: any[]) => void>(
  callback: T,
  delay: number = 500
): DebouncedFunction<T> {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // memoize the debounced callback to ensure it doesn't change unless dependencies change
  const debouncedCallback = useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [delay, callback]
  ) as DebouncedFunction<T>;

  // clear timeout when component unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedCallback;
}

export default useDebounceCallback;
